// Token balance for not-signed-in ("guest") players, kept in localStorage so it
// survives refreshes/closes the way the active run does. Guests start with a
// small balance and can earn more by sharing; they can't buy. When they sign
// in, this balance is migrated into their account once (see claim_signup_bonus
// in schema.sql) and then cleared.

const TOKENS_KEY = "heroOrAVillain:guestTokens";
const SHARED_KEY = "heroOrAVillain:guestSharedScores";
export const GUEST_STARTING_TOKENS = 10;

// Reads the guest balance, lazily seeding the starting amount on first access.
export function getGuestTokens(): number {
  if (typeof window === "undefined") return GUEST_STARTING_TOKENS;
  const raw = window.localStorage.getItem(TOKENS_KEY);
  if (raw === null) {
    window.localStorage.setItem(TOKENS_KEY, String(GUEST_STARTING_TOKENS));
    return GUEST_STARTING_TOKENS;
  }
  const n = parseInt(raw, 10);
  return Number.isFinite(n) ? Math.max(0, n) : GUEST_STARTING_TOKENS;
}

export function setGuestTokens(n: number): void {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(TOKENS_KEY, String(Math.max(0, Math.floor(n))));
}

// Adds (or with a negative amount, removes) guest tokens. Returns the new total.
export function addGuestTokens(n: number): number {
  const next = Math.max(0, getGuestTokens() + n);
  setGuestTokens(next);
  return next;
}

// Wipes guest token state — called after migrating into a signed-in account.
export function clearGuestTokens(): void {
  if (typeof window === "undefined") return;
  window.localStorage.removeItem(TOKENS_KEY);
  window.localStorage.removeItem(SHARED_KEY);
}

// Has this guest already earned the share bonus for a given result? (Mirrors
// the once-per-score rule the server enforces for signed-in players.)
export function hasGuestShared(scoreId: string): boolean {
  if (typeof window === "undefined") return false;
  try {
    const set = JSON.parse(window.localStorage.getItem(SHARED_KEY) ?? "[]");
    return Array.isArray(set) && set.includes(scoreId);
  } catch {
    return false;
  }
}

export function markGuestShared(scoreId: string): void {
  if (typeof window === "undefined") return;
  try {
    const raw = JSON.parse(window.localStorage.getItem(SHARED_KEY) ?? "[]");
    const arr: string[] = Array.isArray(raw) ? raw : [];
    if (!arr.includes(scoreId)) arr.push(scoreId);
    window.localStorage.setItem(SHARED_KEY, JSON.stringify(arr));
  } catch {
    window.localStorage.setItem(SHARED_KEY, JSON.stringify([scoreId]));
  }
}
